/**
 * Typed client for the /api envelope `{ success, data, error }`.
 * Same-origin requests carry the session cookie (credentials: "include"). The browser adds
 * the Origin header on its own, and the API's CSRF check reads it, so it is never set here.
 */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export const NETWORK_ERROR_CODE = "network_error";
export const BAD_RESPONSE_CODE = "bad_response";
export const UNKNOWN_ERROR_CODE = "unknown_error";

/** A failed API call. `code` is the stable machine code; the UI translates it. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
}

function isEnvelope(
  value: unknown,
): value is { success: boolean; data: unknown; error: { code: string; message: string } | null } {
  return typeof value === "object" && value !== null && "success" in value;
}

/** Unwraps the envelope: returns `data` on success, throws ApiError otherwise. */
export function unwrapEnvelope<T>(status: number, raw: unknown): T {
  if (!isEnvelope(raw)) {
    throw new ApiError(status, BAD_RESPONSE_CODE, "Unexpected response from the server");
  }
  if (raw.success && raw.error === null) {
    return raw.data as T;
  }
  const error = raw.error ?? { code: UNKNOWN_ERROR_CODE, message: "Request failed" };
  throw new ApiError(status, error.code, error.message);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = { method, credentials: "include", headers };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    throw new ApiError(0, NETWORK_ERROR_CODE, "Network error");
  }
  const raw: unknown = await response.json().catch(() => null);
  return unwrapEnvelope<T>(response.status, raw);
}

/** Probe helper: resolves to the HTTP status and code without throwing on refusals. */
export async function probeRequest(
  path: string,
  options: RequestOptions = {},
): Promise<{ status: number; code: string | null }> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = { method: options.method ?? "GET", credentials: "include", headers };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  try {
    const response = await fetch(path, init);
    const raw: unknown = await response.json().catch(() => null);
    const code = isEnvelope(raw) && raw.error ? raw.error.code : null;
    return { status: response.status, code };
  } catch {
    return { status: 0, code: NETWORK_ERROR_CODE };
  }
}
